import { Box } from '@eatclub-apps/ec-component-library';
import { useTheme } from '@material-ui/core';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { ReactComponent as IconDate } from '../../../../../assets/icons/date.svg';
import { ReactComponent as IconGuests } from '../../../../../assets/icons/guests.svg';
import { ReactComponent as IconTime } from '../../../../../assets/icons/time.svg';
import { Button } from '../../../../../components/Button';
import { Modal } from '../../../../../components/Modal';
import { Spacer } from '../../../../../components/Spacer';
import { Typography } from '../../../../../components/Typography';
import {
  updateBookingAction,
  updateBookingStepAction,
} from '../../../../../data/actions/bookingAction';
import { fetchBookingTermsAction } from '../../../../../data/actions/bookingTermsAction';
import { freeBookingAction } from '../../../../../data/actions/obeeBookingAction';
import {
  bookingKeys,
  bookingPropTypes,
  bookingSteps,
  isEditing,
} from '../../../../../data/models/Booking';
import { bookingTermsPropTypes } from '../../../../../data/models/BookingTerms';
import { addonGroupsPropTypes } from '../../../../../data/models/Experience';
import { restaurantPropTypes } from '../../../../../data/models/Restaurant';
import {
  getSessionsForBooking,
  getSessionsForFilter,
} from '../../../../../data/models/obeeSessions';
import { trackEvent } from '../../../../../utils/analytics';
import { devLog } from '../../../../../utils/devLog';
import {
  filterArrayUnique,
  filterPastTimes,
  formatDateToMoment,
  formatIntFromTime,
  getMinMaxInArrayOfObjects,
  isEmpty,
  range,
  sortListNatural,
  timeStringToTime,
} from '../../../../../utils/helpers';
import { useIsMobile } from '../../../../../utils/hooks';
import { safeParse } from '../../../../../utils/stringHelpers';
import { Carousel } from '../../Carousel';

const JoinWaitlistModal = ({
  booking,
  close,
  open,
  restaurant,
  updateBooking,
  updateBookingStep,
  freeBooking,
  fetchBookingTerms,
  bookingTerms,
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const [errorMessage, setErrorMessage] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const systemTimeInterval =
    safeParse(restaurant?.data?.bookingWidgetSettings)?.widgetSettings?.systemTimeInterval ?? 15;

  const allTimes = useMemo(() => {
    const sessionsForFilter = getSessionsForFilter(
      getSessionsForBooking(restaurant.data.areas, booking),
      booking,
    );

    // TODO if an experience was chosen that has sitting times, use sitting times for that experience

    // Get the list of times from sessions with sitting times
    const sittingTimes = sessionsForFilter
      .filter((session) => !isEmpty(session?.sittingTimes))
      .flatMap((session) => session?.sittingTimes)
      .flatMap((sitting) => sitting?.times);
    const uniqueSittingTimes = filterArrayUnique(sittingTimes).map((timeString) =>
      formatIntFromTime(timeStringToTime(timeString, 'h:mma')),
    );

    // For all normal sessions, get the range from the start to end time
    let sittingTimesFromRange = [];
    const regularSittings = sessionsForFilter.filter((session) => isEmpty(session?.sittingTimes));
    if (!isEmpty(regularSittings)) {
      const { min, max } = getMinMaxInArrayOfObjects(regularSittings, 'startTime', 'endTime');
      sittingTimesFromRange = range(min, max, systemTimeInterval, false);
    }

    const times = sortListNatural(
      filterArrayUnique([...uniqueSittingTimes, ...sittingTimesFromRange]),
    );
    // This is to prevent the carousel from showing past times
    const filteredTimes = filterPastTimes(times, booking.date, restaurant.data.timezone);
    // note: -1 is "All times"
    return filteredTimes.map((time) => ({ time }));
  }, [
    booking.areaId,
    booking.date,
    booking.sessionId,
    restaurant.data.areas,
    restaurant.data.timezone,
  ]);

  const showTermsSecondStep = safeParse(restaurant?.data?.bookingWidgetSettings)?.widgetSettings
    ?.settingsDetails?.showTermsSecondStep;

  useEffect(() => {
    // If there is a need to jump to Term page, only proceed after fetched bookingTerms
    if (showTermsSecondStep && bookingTerms?.pending) {
      return;
    }

    if (
      submitting &&
      showTermsSecondStep &&
      bookingTerms?.success &&
      !isEmpty(bookingTerms?.data)
    ) {
      updateBookingStep(bookingSteps.terms);
      setSubmitting(false);
    }
  }, [showTermsSecondStep, bookingTerms?.pending]);

  const submit = async () => {
    trackEvent('Navigation', 'Updating Step', 'Waitlist Next Button');

    if (isEmpty(booking?.availabilitySuggestion?.time)) {
      setErrorMessage('Please select a time');
      return;
    }

    // Safety net to free a held booking if somehow it's still held
    let heldBookingId = booking?.heldBookingId;
    if (!isEditing(booking) && heldBookingId) {
      await freeBooking(heldBookingId);
      heldBookingId = null;
    }

    setSubmitting(true);
    updateBooking('waitlist', true);
    if (showTermsSecondStep) {
      fetchBookingTerms(
        booking?.availabilitySuggestion?.table?.areaId,
        booking?.date,
        booking?.availabilitySuggestion?.time,
        booking?.guests,
      );
    } else {
      updateBookingStep(bookingSteps.details);
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (showTermsSecondStep) {
      fetchBookingTerms(
        booking?.availabilitySuggestion?.table?.areaId,
        booking?.date,
        booking?.availabilitySuggestion?.time,
        booking?.guests,
      );
    }
  }, [showTermsSecondStep]);

  return (
    <Modal
      open={open}
      onClose={() => {
        updateBooking(bookingKeys.availabilitySuggestion, null);
        close();
      }}
      style={{
        header: {
          backgroundColor: theme.colors.availabilityBackgroundBoxBgColour,
        },
        content: {
          backgroundColor: theme.colors.availabilityBackgroundBoxBgColour,
        },
        footer: {
          backgroundColor: theme.colors.availabilityBackgroundBoxBgColour,
        },
      }}
      title='Join the waitlist'
      footer={
        <>
          <Button
            size='large'
            variant='outlined'
            onClick={() => {
              close();
            }}
            fullWidth={isMobile}
          >
            Cancel
          </Button>
          <Button size='large' onClick={submit} fullWidth={isMobile} color='primary'>
            {showTermsSecondStep ? 'Next: Terms and conditions' : 'Next: Add contact details'}
          </Button>
        </>
      }
    >
      <Spacer direction='vertical' gap={40}>
        <Spacer direction='vertical' gap={24}>
          <Spacer direction='vertical'>
            <Typography style={{ fontWeight: theme.fontWeights.medium }}>
              {restaurant.data.name}
            </Typography>
            <Typography>{restaurant.data.address}</Typography>
          </Spacer>
          <Spacer gap='m' direction={isMobile ? 'vertical' : 'horizontal'}>
            <Spacer style={{ alignItems: 'center' }} gap='s'>
              <IconDate fill={restaurant.data.iconBgColour} />
              <Typography>{formatDateToMoment(booking.date).format('dddd, Do of MMMM')}</Typography>
            </Spacer>
            <Spacer style={{ alignItems: 'center' }} gap='s'>
              <IconGuests fill={restaurant.data.iconBgColour} />
              <Typography>
                {`${booking.guests} ${booking.guests > 1 ? 'guests' : 'guest'}`}
              </Typography>
            </Spacer>
            {booking.sessionId !== -1 && (
              <Spacer style={{ alignItems: 'center' }} gap='s'>
                <IconTime fill={restaurant.data.iconBgColour} />
                <Typography>{booking.sessionLabel}</Typography>
              </Spacer>
            )}
          </Spacer>
        </Spacer>
        <Spacer direction='vertical' gap={24}>
          <Typography variant='h4'>Select preferred time</Typography>

          <Spacer direction='vertical' gap={2}>
            {errorMessage && <Box style={{ color: theme.colors.valencia }}>{errorMessage}</Box>}
            <Carousel
              rows={isMobile ? 4 : 2}
              columns={isMobile ? 2 : 4}
              items={allTimes}
              mobileButtonsOnly
              skipAreaSelection
              isWaitList
            />
          </Spacer>
        </Spacer>
      </Spacer>
    </Modal>
  );
};

JoinWaitlistModal.defaultProps = {
  open: false,
};

JoinWaitlistModal.propTypes = {
  booking: bookingPropTypes.isRequired,
  close: PropTypes.func.isRequired,
  open: PropTypes.bool,
  restaurant: restaurantPropTypes.isRequired,
  updateBooking: PropTypes.func.isRequired,
  updateBookingStep: PropTypes.func.isRequired,
  freeBooking: PropTypes.func.isRequired,
  fetchBookingTerms: PropTypes.func.isRequired,
  bookingTerms: bookingTermsPropTypes.isRequired,
};

const mapStateToProps = (state) => ({
  booking: state.booking,
  obeeBooking: state.obeeBooking,
  restaurant: state.restaurant,
  bookingTerms: state.bookingTerms,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      updateBooking: updateBookingAction,
      updateBookingStep: updateBookingStepAction,
      freeBooking: freeBookingAction,
      fetchBookingTerms: fetchBookingTermsAction,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(JoinWaitlistModal);
